import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Box,
  CheckIcon,
  Column,
  Heading,
  Menu,
  MenuItem,
  MenuList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  PromptInputMenuButton,
  Row,
  StylesIcon,
  Text,
} from "@hightouch/htui";
import type { ComposerLeftAction } from "src/components/llm/components/composer-left-actions";
import {
  buildStylePreviewImages,
  type StylePreviewImage,
} from "src/pages/context-hub/styles/types";
import {
  AgentScope,
  type StyleEntitiesQuery,
  useStyleEntitiesQuery,
} from "src/graphql/types";
import { isStyleOffered } from "src/pages/ad-studio/components/is-style-offered";
import { isInBrandScope } from "src/utils/brand-scope";
import {
  AUTO_LABEL,
  NO_STYLE_LABEL,
  styleSelectionLabel,
} from "src/pages/ad-studio/components/style-selection-label";
import { AdStudioImage } from "./ad-studio-image";

// Mirrors the context-pill popovers in compact-chat-input: delay opening so
// quick mouse travel through the menu doesn't flash popovers, skipped when a
// preview closed moments ago.
const HOVER_PREVIEW_OPEN_DELAY_MS = 400;
const HOVER_PREVIEW_SKIP_DELAY_WINDOW_MS = 500;

const PREVIEW_IMAGE_COUNT = 3;

const PREVIEW_IMAGE_ROW_HEIGHT = "132px";

// Compensates for the MenuList's 8px padding around the anchor box so the
// popover aligns with the menu's outer top edge with an 8px gap on the left.
const PREVIEW_POPOVER_OFFSET: [number, number] = [-8, 16];

// Space the menu occupies beyond the scroll box: popper gutter (8) +
// MenuList vertical padding (16) + border, plus breathing room so the menu
// doesn't kiss the viewport edge.
const MENU_VIEWPORT_CHROME_PX = 40;

// Below this the menu is unusably squashed; matches roughly what HT UI's
// Select shows (~320px popover) at its smallest.
const MENU_MIN_MAX_HEIGHT_PX = 200;

const MENU_MAX_HEIGHT_VIEWPORT_RATIO = 0.6;

// Chakra's popper flips the menu to whichever side of the trigger fits, but
// never resizes it — so a fixed maxH taller than the available space lets
// content that is under the cap but over the space overflow the viewport
// without ever showing a scrollbar. Cap the scroll box at the space on the
// roomier side of the trigger instead: if popper keeps the smaller side, the
// content already fit there; if it flips, this cap fits by construction.
export function computeStyleMenuMaxHeight({
  triggerTop,
  triggerBottom,
  viewportHeight,
}: {
  triggerTop: number;
  triggerBottom: number;
  viewportHeight: number;
}): number {
  const available =
    Math.max(triggerTop, viewportHeight - triggerBottom) -
    MENU_VIEWPORT_CHROME_PX;
  const ceiling = Math.round(viewportHeight * MENU_MAX_HEIGHT_VIEWPORT_RATIO);
  return Math.max(MENU_MIN_MAX_HEIGHT_PX, Math.min(ceiling, available));
}

type StyleEntityRow = StyleEntitiesQuery["styleEntities"][number];

export type StyleOption = Pick<
  StyleEntityRow,
  | "id"
  | "name"
  | "description"
  | "markdown"
  | "cites"
  | "isActive"
  | "agentScopes"
>;

// The entity query returns every current style for the workspace, so the
// legacy server-side filter (active, ad-surface scope, brand profile match or
// unassigned, newest first) moves client-side. An empty `brandProfiles`
// assignment means "applies to all brand profiles".
// The selected style is kept even as a draft or on another surface, so a chat
// that explicitly uses one (e.g. "Test in Ad Studio") doesn't fall back to Auto.
const AD_SURFACES = [AgentScope.Ads];

export function selectAdSurfaceStyleOptions(
  styles: StyleEntityRow[],
  brandProfileId: string,
  selectedStyleId?: string,
  surfaces: readonly AgentScope[] = AD_SURFACES,
): StyleOption[] {
  return styles
    .filter(
      (style) =>
        isStyleOffered(style, surfaces, selectedStyleId) &&
        isInBrandScope(
          style.brandProfiles.map((profile) => profile.id),
          [brandProfileId],
        ),
    )
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export type StyleSelection =
  | { kind: "auto" }
  | { kind: "none" }
  | { kind: "style"; id: string };

// Loosely typed because it is read off persisted jsonb columns.
export type StyleSelectionContext = {
  adStudioStyleId?: string | null;
  adStudioStyleMode?: string | null;
};

export function styleSelectionToContext(s: StyleSelection): {
  adStudioStyleId?: string;
  adStudioStyleMode?: "auto" | "none";
} {
  switch (s.kind) {
    case "style":
      return { adStudioStyleId: s.id };
    case "auto":
      return { adStudioStyleMode: "auto" };
    case "none":
      return { adStudioStyleMode: "none" };
  }
}

export function styleSelectionFromContext(
  ctx: StyleSelectionContext | null | undefined,
): StyleSelection {
  if (ctx?.adStudioStyleId) {
    return { kind: "style", id: ctx.adStudioStyleId };
  }
  if (ctx?.adStudioStyleMode === "none") {
    return { kind: "none" };
  }
  return { kind: "auto" };
}

// Same image order the Context Hub gallery cards use, so the grid shrinks to
// however many images the style has.
export function buildStyleHoverPreviewItems(
  style: Pick<StyleOption, "markdown" | "cites">,
): StylePreviewImage[] {
  return buildStylePreviewImages({
    cites: style.cites,
    markdown: style.markdown,
    count: PREVIEW_IMAGE_COUNT,
  });
}

type StyleOptionsArgs = {
  brandProfileId: string | undefined;
  value: StyleSelection;
  onChange: (selection: StyleSelection) => void;
  /** The agents whose styles are offered; a stable array. Defaults to ads. */
  surfaces?: readonly AgentScope[];
};

/**
 * The styles a picker offers the brand profile, and the label for the current
 * choice. Falls back to Auto when the chosen style goes away.
 */
export function useStyleOptions({
  brandProfileId,
  value,
  onChange,
  surfaces = AD_SURFACES,
}: StyleOptionsArgs): { styles: StyleOption[] | undefined; label: string } {
  const selectedStyleId = value.kind === "style" ? value.id : undefined;
  // select depends on props, so wrap it for referential stability.
  const selectStyleOptions = useCallback(
    (data: StyleEntitiesQuery): StyleOption[] =>
      selectAdSurfaceStyleOptions(
        data.styleEntities,
        brandProfileId ?? "",
        selectedStyleId,
        surfaces,
      ),
    [brandProfileId, selectedStyleId, surfaces],
  );
  const { data: styles } = useStyleEntitiesQuery(undefined, {
    enabled: Boolean(brandProfileId),
    select: selectStyleOptions,
  });

  // Fall back to Auto if the selected style no longer exists (deleted, or the
  // brand profile changed). Only once a brand profile is known: before then the
  // cached query filters against an empty profile id, which would wipe a
  // caller-seeded selection (e.g. "Remix in a new style") before the default
  // profile loads.
  useEffect(() => {
    if (!brandProfileId || value.kind !== "style" || !styles) return;
    if (!styles.some((style) => style.id === value.id)) {
      onChange({ kind: "auto" });
    }
  }, [brandProfileId, value, styles, onChange]);

  const label = styleSelectionLabel(value, styles);
  return { styles: brandProfileId ? styles : undefined, label };
}

type Props = StyleOptionsArgs & {
  /** Where the pill's label starts fading; narrow composers pass less. */
  maxLabelWidth?: string;
};

export function useAdStudioStylePicker({
  brandProfileId,
  value,
  onChange,
  surfaces,
  maxLabelWidth,
}: Props): {
  element: ReactNode;
  action: ComposerLeftAction | undefined;
} {
  const { styles, label } = useStyleOptions({
    brandProfileId,
    value,
    onChange,
    surfaces,
  });
  // Measured from the trigger each time the menu opens, since the pill's
  // position varies by surface (bottom chat bar vs mid-page landing input).
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const [menuMaxHeight, setMenuMaxHeight] = useState(MENU_MIN_MAX_HEIGHT_PX);
  const handleMenuOpen = useCallback(() => {
    setMenuMaxHeight(measureStyleMenuMaxHeight(menuContainerRef.current));
  }, []);

  if (!styles || styles.length === 0) {
    return { element: null, action: undefined };
  }

  return {
    element: (
      // display="contents" keeps the wrapper out of the pill row's flex layout;
      // it exists only to find the trigger button when the menu opens.
      <Box display="contents" ref={menuContainerRef}>
        <Menu onOpen={handleMenuOpen}>
          <PromptInputMenuButton
            variant="compact"
            icon={StylesIcon}
            label={label}
            isActive={value.kind !== "auto"}
            tooltip={`Style: ${label}`}
            ariaLabel={`Style: ${label}`}
            maxLabelWidth={maxLabelWidth}
          />
          <MenuList>
            <StyleMenuOptions
              styles={styles}
              value={value}
              onChange={onChange}
              maxHeight={menuMaxHeight}
            />
          </MenuList>
        </Menu>
      </Box>
    ),
    action: {
      key: "style",
      label: `Style: ${label}`,
      icon: StylesIcon,
      compactOnly: true,
      isChecked: value.kind !== "auto",
      menuTitle: "Style",
      menu: (
        <ComposerStyleMenuOptions
          styles={styles}
          value={value}
          onChange={onChange}
        />
      ),
    },
  };
}

export const AdStudioStyleSelect: FC<Props> = (props) =>
  useAdStudioStylePicker(props).element;

function ComposerStyleMenuOptions(
  props: Pick<StyleMenuOptionsProps, "styles" | "value" | "onChange">,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(MENU_MIN_MAX_HEIGHT_PX);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const menu = container?.closest('[role="menu"]');
    const triggerId = menu?.getAttribute("aria-labelledby");
    const trigger = triggerId ? document.getElementById(triggerId) : null;
    if (!container || !menu || !trigger) return;

    const rect = trigger.getBoundingClientRect();
    // The parent menu owns the Back row above these scrolling choices.
    const headerHeight =
      container.getBoundingClientRect().top - menu.getBoundingClientRect().top;
    setMaxHeight(
      Math.max(
        0,
        computeStyleMenuMaxHeight({
          triggerTop: rect.top,
          triggerBottom: rect.bottom,
          viewportHeight: window.innerHeight,
        }) - headerHeight,
      ),
    );
  }, []);

  return (
    <Box ref={containerRef}>
      <StyleMenuOptions {...props} maxHeight={maxHeight} header={false} />
    </Box>
  );
}

/** How tall a style menu may grow, from the trigger inside `container`; the portaled list lives outside it. */
export function measureStyleMenuMaxHeight(container: HTMLElement | null) {
  const trigger = container?.querySelector("button");
  if (!trigger) return MENU_MIN_MAX_HEIGHT_PX;
  const rect = trigger.getBoundingClientRect();
  return computeStyleMenuMaxHeight({
    triggerTop: rect.top,
    triggerBottom: rect.bottom,
    viewportHeight: window.innerHeight,
  });
}

type StyleMenuOptionsProps = {
  styles: StyleOption[];
  value: StyleSelection;
  onChange: (selection: StyleSelection) => void;
  maxHeight: number;
  /** Offers "No style". Defaults to true. */
  allowNone?: boolean;
  /** Replaces the "Select style" heading, such as with a way back to a parent menu. */
  header?: ReactNode;
};

/** The style choices for inside a `MenuList`, each previewed on hover. */
export function StyleMenuOptions({
  styles,
  value,
  onChange,
  maxHeight,
  allowNone = true,
  header,
}: StyleMenuOptionsProps) {
  const [hoveredStyleId, setHoveredStyleId] = useState<string | null>(null);
  const lastClosedAtRef = useRef(0);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keeps the popover's content populated during the close transition
  // instead of flashing empty.
  const lastPreviewStyleIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (hoveredStyleId) lastPreviewStyleIdRef.current = hoveredStyleId;
  }, [hoveredStyleId]);

  const handleStyleEnter = useCallback((styleId: string) => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    const skipDelay =
      Date.now() - lastClosedAtRef.current < HOVER_PREVIEW_SKIP_DELAY_WINDOW_MS;
    if (skipDelay) {
      setHoveredStyleId(styleId);
    } else {
      openTimerRef.current = setTimeout(() => {
        setHoveredStyleId(styleId);
      }, HOVER_PREVIEW_OPEN_DELAY_MS);
    }
  }, []);

  const handleStyleLeave = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    setHoveredStyleId((current) => {
      if (current !== null) lastClosedAtRef.current = Date.now();
      return null;
    });
  }, []);

  useEffect(
    () => () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
    },
    [],
  );

  const isAutoSelected = value.kind === "auto";
  const isNoneSelected = value.kind === "none";
  const previewStyleId = hoveredStyleId ?? lastPreviewStyleIdRef.current;
  const previewStyle = styles.find((style) => style.id === previewStyleId);

  return (
    <Popover
      isOpen={hoveredStyleId !== null}
      placement="left-start"
      offset={PREVIEW_POPOVER_OFFSET}
      isLazy
      lazyBehavior="keepMounted"
      autoFocus={false}
    >
      <PopoverTrigger>
        {/* MenuList only accepts children, so cap the height and scroll
            on its sole inline child — the element Chakra's Popper sizes
            and positions shrink-wraps to this box. The cap tracks the
            measured space around the trigger (see
            computeStyleMenuMaxHeight); a fixed cap overflows the
            viewport whenever content is under the cap but over the
            actual available space. The hover preview lives in a Portal
            below, so it is excluded. */}
        <Box maxH={`${maxHeight}px`} overflowY="auto">
          {header ?? (
            <Box px={2} py={0.5}>
              <Text color="text.secondary" size="sm" fontWeight="medium">
                Select style
              </Text>
            </Box>
          )}
          <MenuItem onClick={() => onChange({ kind: "auto" })}>
            <Row justify="space-between" align="center" width="100%">
              <Text>{AUTO_LABEL}</Text>
              {isAutoSelected && <CheckIcon color="text.secondary" />}
            </Row>
          </MenuItem>
          {styles.map((style) => (
            <Box
              key={style.id}
              onMouseEnter={() => handleStyleEnter(style.id)}
              onMouseLeave={handleStyleLeave}
            >
              <MenuItem
                onClick={() => {
                  handleStyleLeave();
                  onChange({ kind: "style", id: style.id });
                }}
              >
                <Row justify="space-between" align="center" width="100%">
                  <Row align="center" gap={2} minWidth={0}>
                    <Text>{style.name}</Text>
                    {!style.isActive && <Badge size="sm">Draft</Badge>}
                  </Row>
                  {value.kind === "style" && value.id === style.id && (
                    <CheckIcon color="text.secondary" />
                  )}
                </Row>
              </MenuItem>
            </Box>
          ))}
          {allowNone && (
            <MenuItem onClick={() => onChange({ kind: "none" })}>
              <Row justify="space-between" align="center" width="100%">
                <Text color="text.secondary">{NO_STYLE_LABEL}</Text>
                {isNoneSelected && <CheckIcon color="text.secondary" />}
              </Row>
            </MenuItem>
          )}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          width="400px"
          border="none"
          borderRadius="2xl"
          overflow="hidden"
          bg="white"
          boxShadow="lg"
          pointerEvents="none"
        >
          {previewStyle && <StyleHoverPreviewContent style={previewStyle} />}
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

const StyleHoverPreviewContent: FC<{ style: StyleOption }> = ({ style }) => {
  // Zod-parses two jsonb columns, so keep it off the per-render hot path while
  // the menu re-renders on every hover change.
  const previews = useMemo(() => buildStyleHoverPreviewItems(style), [style]);

  const columnCount = Math.min(previews.length, PREVIEW_IMAGE_COUNT);

  return (
    <>
      <Column px={6} py={5} gap={1}>
        <Heading size="lg">{style.name}</Heading>
        {style.description && (
          <Box
            noOfLines={3}
            color="text.secondary"
            fontSize="md"
            lineHeight="5"
          >
            {style.description}
          </Box>
        )}
      </Column>
      {previews.length > 0 && (
        <Box
          display="grid"
          gridTemplateColumns={`repeat(${columnCount}, 1fr)`}
          gap="4px"
        >
          {previews.map((preview) => (
            <Box
              key={preview.id}
              height={PREVIEW_IMAGE_ROW_HEIGHT}
              overflow="hidden"
              bg="base.background"
            >
              <AdStudioImage
                src={preview.src}
                alt={preview.alt}
                width="100%"
                height="100%"
                objectFit="cover"
                naturalWidth={Math.ceil(800 / columnCount)}
              />
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};
